// Column references as written in DML (`o.total`, `[Order Total]`, `"t"."Qty"`)
// compared the way each engine resolves them: PostgreSQL and Oracle fold an
// unquoted name (to lower / upper case) and match a quoted one exactly; SQL
// Server, MySQL and SQLite compare column names without case. Used by the
// Visual Query Editor's DML column pickers (already-set columns, unknown
// columns, duplicate guard).

import type { SqlEngine } from '../../sql-grammar/model';

// `[qualifier.]name` where the name may be delimited ("a.b", `a`, [a b]) —
// the greedy qualifier backtracks until the tail is one whole name part.
const COLUMN_REF_RE = /^(?:(.*)\.)?\s*("(?:[^"]|"")*"|`(?:[^`]|``)*`|\[(?:[^\]]|\]\])*\]|[^."`[\]]+)$/;

/**
 * Splits a written column reference (`o.total`, `[Order Total]`, `"t"."Qty"`)
 * into its qualifier text and its terminal name, delimiters removed. The
 * structure's assignment targets can't be used for this: a qualified target
 * parses with the qualifier as its name.
 */
export function splitColumnRef(raw: string): {
  readonly qualifier: string | null;
  readonly name: string;
  readonly quoted: boolean;
} {
  const match = COLUMN_REF_RE.exec(raw.trim());
  const part = match ? match[2].trim() : raw.trim();
  const quoted = /^["`[]/.test(part);
  // A doubled closing delimiter inside the name is an escaped one ("a""b" → a"b).
  const close = part[part.length - 1];
  return {
    qualifier: match?.[1]?.trim() || null,
    name: quoted ? part.slice(1, -1).split(close + close).join(close) : part,
    quoted,
  };
}

/**
 * Same column, by the engine's identifier rules? PostgreSQL and Oracle match a
 * delimited reference exactly and fold a bare one (to lower / upper case, so
 * bare `name` is not `"Name"`); SQL Server, MySQL and SQLite compare column
 * names without case either way.
 */
export function isSameColumn(ref: string, catalogName: string, engine: SqlEngine): boolean {
  const { name, quoted } = splitColumnRef(ref);
  if (engine !== 'postgres' && engine !== 'oracle') return name.toLowerCase() === catalogName.toLowerCase();
  return (quoted ? name : foldBare(name, engine)) === catalogName;
}

/** Two written references to the same column (`StatusCd`, `[StatusCd]`, `t.statuscd`)? */
export function isSameRef(a: string, b: string, engine: SqlEngine): boolean {
  const right = splitColumnRef(b);
  return isSameColumn(a, right.quoted ? right.name : foldBare(right.name, engine), engine);
}

function foldBare(name: string, engine: SqlEngine): string {
  return engine === 'postgres' ? name.toLowerCase() : engine === 'oracle' ? name.toUpperCase() : name;
}
